import React, { useEffect, useState, useRef, useCallback } from "react"; // Added useCallback
import { SourceNodeData, HandleConfig } from "../../types"; // Added HandleConfig
import { Position } from "reactflow"; // Added Position
import ConfigBoxToolbar from "../../components/shared/ConfigBoxToolbar";
import LabelWithVisibilityToggle, { VisibilityOption } from "../../components/shared/LabelWithVisibilityToggle";
import AutoComplete from "../../components/shared/AutoComplete";
import HandleManager from "../../components/shared/HandleManager"; // Import HandleManager

interface MobileSDKConfigBoxProps {
  nodeId: string; // Add nodeId prop
  data: SourceNodeData;
  onUpdate?: (data: Partial<SourceNodeData>) => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  onDetachFromGroup?: () => void; // Add prop
  isAdvancedMode?: boolean;
  portalRoot?: HTMLElement | null;
}

const EVENT_VOLUMES = [
  { value: "0-10M", label: "<10M Events" },
  { value: "10-50M", label: "10-50M Events" },
  { value: "50-100M", label: "50-100M Events" },
  { value: "100-500M", label: "100-500M Events" },
  { value: "500M+", label: "500M+ Events" },
];

const PLATFORM_SUGGESTIONS = [
  { value: "iOS", label: "iOS" },
  { value: "Android", label: "Android" },
  { value: "React Native", label: "React Native" },
  { value: "Flutter", label: "Flutter" },
  { value: "Xamarin", label: "Xamarin" },
  { value: "Unity", label: "Unity" },
  { value: "Cordova", label: "Cordova" },
  { value: "Ionic", label: "Ionic" },
  { value: "NativeScript", label: "NativeScript" },
];

const CUSTOM_LABEL_SUGGESTIONS = [
  { value: "Production", label: "Production" },
  { value: "Development", label: "Development" },
  { value: "Staging", label: "Staging" },
  { value: "Beta", label: "Beta" },
  { value: "Internal", label: "Internal" },
  { value: "High Priority", label: "High Priority" },
  { value: "Low Priority", label: "Low Priority" },
  { value: "Critical", label: "Critical" },
  { value: "Experimental", label: "Experimental" },
  { value: "Legacy", label: "Legacy" },
  { value: "New", label: "New" },
  { value: "Deprecated", label: "Deprecated" },
  { value: "Custom", label: "Custom" },
];

export default function MobileSDKConfigBox({
  nodeId, // Destructure nodeId
  data,
  onUpdate,
  onDelete,
  onDuplicate,
  onDetachFromGroup, // Destructure prop
  portalRoot,
}: MobileSDKConfigBoxProps) {
  // Initialize state with props
  const [label, setLabel] = useState(data.label || "");
  const [eventVolume, setEventVolume] = useState(data.eventVolume || "");
  const [platform, setPlatform] = useState(data.eventSpec || "");
  const [customLabel, setCustomLabel] = useState(data.otherConsentToolName || "");
  const [notes, setNotes] = useState(data.notes || ""); // Add notes state


  // --- Default Handles for Backward Compatibility ---
  const defaultHandles: HandleConfig[] = [
    { id: 'default-source', type: 'source', position: Position.Right, color: '#3B82F6' }, // Blue
    { id: 'default-target', type: 'target', position: Position.Left, color: '#6366F1' }, // Indigo
  ];
  // --- End Default Handles ---

  // --- Add State for Handles ---
  const [handles, setHandles] = useState<HandleConfig[]>(() =>
    Array.isArray(data.handles) ? [...data.handles] : defaultHandles
  );
  // --- End Add State for Handles ---

  // NOTE: Removed isOldVersion state. Assume new handle system for this node type.
  // The handles state will be initialized with defaults if data.handles is missing.

  // Initialize visibility settings
  const [eventVolumeVisibility, setEventVolumeVisibility] = useState<VisibilityOption>(
    data.eventVolumeVisibility === "hidden" ? "hidden" : "visible"
  );
  const [platformVisibility, setPlatformVisibility] = useState<VisibilityOption>(
    data.eventSpecVisibility === "hidden" ? "hidden" : "visible"
  );
  const [customLabelVisibility, setCustomLabelVisibility] = useState<VisibilityOption>(
    data.consentToolVisibility === "hidden" ? "hidden" : "visible"
  );
  const [notesVisibility, setNotesVisibility] = useState<VisibilityOption>(data.notesVisibility ?? "visible"); // Add notes visibility state
  
  // Track previous update to prevent loops
  const prevUpdateRef = useRef("");
  // Track if component has initialized to prevent effect firing on mount
  const isInitialized = useRef(false);


  // Effect to synchronize state with data props
  useEffect(() => {
    // Sync non-handle fields
    setLabel(data.label || "");
    setEventVolume(data.eventVolume || "");
    setPlatform(data.eventSpec || "");
    setCustomLabel(data.otherConsentToolName || "");
    setNotes(data.notes || "");
    setEventVolumeVisibility(data.eventVolumeVisibility === "hidden" ? "hidden" : "visible");
    setPlatformVisibility(data.eventSpecVisibility === "hidden" ? "hidden" : "visible");
    setCustomLabelVisibility(data.consentToolVisibility === "hidden" ? "hidden" : "visible");
    setNotesVisibility(data.notesVisibility ?? "visible");

    // Sync handles state more carefully
    if (Array.isArray(data.handles) && JSON.stringify(data.handles) !== JSON.stringify(handles)) {
        setHandles([...data.handles]);
    }
    // Do not reset to defaultHandles here

    // Mark as initialized after the first sync
    if (!isInitialized.current) {
        isInitialized.current = true;
    }
  }, [data]); // Rerun only when data prop changes

  // Update parent when state changes
  useEffect(() => {
    // Prevent firing on initial mount/effect synchronization
    if (!isInitialized.current) {
      isInitialized.current = true;
      return;
    }

    const updates = {
      label,
      eventVolume,
      eventSpec: platform,
      otherConsentToolName: customLabel,
      eventVolumeVisibility,
      eventSpecVisibility: platformVisibility,
      consentToolVisibility: customLabelVisibility,
      notes, // Add notes to updates
      notesVisibility, // Add notes visibility to updates
      handles: Array.isArray(handles) ? handles : [], // Ensure handles is treated as array here for safety
    };

    const updatesJson = JSON.stringify(updates);
    if (updatesJson !== prevUpdateRef.current && onUpdate) {
      prevUpdateRef.current = updatesJson;
      onUpdate(updates);
    }
  }, [
    label, 
    eventVolume, 
    platform, 
    customLabel,
    eventVolumeVisibility,
    platformVisibility,
    customLabelVisibility,
    notes, // Add notes dependency
    notesVisibility, // Add notes visibility dependency
    handles, // Add handles dependency
    onUpdate
  ]);


  // Toggle visibility setting
  const toggleVisibility = (
    current: VisibilityOption,
    setter: React.Dispatch<React.SetStateAction<VisibilityOption>>
  ) => {
    const nextVisibility = current === "hidden" ? "visible" : "hidden";
    setter(nextVisibility);
  };

  // --- Handler for HandleManager changes ---
  const handleHandlesChange = useCallback((newHandles: HandleConfig[]) => {
    // Allow setting empty handles
    setHandles(newHandles);
  }, []);
  // --- End Handler ---

  return (
    <div className="space-y-3 relative">
      {/* Add the toolbar - force isAdvancedMode to true to make it always visible */}
      <ConfigBoxToolbar
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onDetachFromGroup={onDetachFromGroup} // Pass prop down
        isAdvancedMode={true}
      />

      <div className="pt-1">
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 mt-3">
          App Name
        </label>
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="My Mobile App"
          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:focus:border-blue-600 focus:ring rounded focus:outline-none focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 dark:bg-gray-700 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out"
        />
      </div>

      <div className="relative">
        <LabelWithVisibilityToggle
          label="Estimated monthly event volume"
          visibility={eventVolumeVisibility}
          onToggleVisibility={() => toggleVisibility(eventVolumeVisibility, setEventVolumeVisibility)}
          className="mb-1"
        />
        <select
          value={eventVolume}
          onChange={(e) => setEventVolume(e.target.value)}
          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:focus:border-blue-600 focus:ring rounded focus:outline-none focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 dark:bg-gray-700 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out"
        >
          <option value="">Select volume</option>
          {EVENT_VOLUMES.map((volume) => (
            <option key={volume.value} value={volume.value}>
              {volume.label}
            </option>
          ))}
        </select>
      </div>

      <div className="relative">
        <LabelWithVisibilityToggle
          label="Platform"
          visibility={platformVisibility}
          onToggleVisibility={() => toggleVisibility(platformVisibility, setPlatformVisibility)}
          className="mb-1"
        />
        <AutoComplete
          value={platform}
          onChange={setPlatform}
          options={PLATFORM_SUGGESTIONS}
          placeholder="Select or enter platform"
          dropdownClassName="absolute z-[100]"
          portalContainer={portalRoot}
        />
      </div>

      <div className="relative">
        <LabelWithVisibilityToggle
          label="Custom Label"
          visibility={customLabelVisibility}
          onToggleVisibility={() => toggleVisibility(customLabelVisibility, setCustomLabelVisibility)}
          className="mb-1"
        />
        <AutoComplete
          value={customLabel}
          onChange={setCustomLabel}
          options={CUSTOM_LABEL_SUGGESTIONS}
          placeholder="Add custom label (optional)"
          dropdownClassName="absolute z-[100]"
          portalContainer={portalRoot}
        />
      </div>

      {/* Notes Section */}
      <div>
        <LabelWithVisibilityToggle
          label="Notes"
          visibility={notesVisibility}
          onToggleVisibility={() => toggleVisibility(notesVisibility, setNotesVisibility)}
          className="mb-1"
        />
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add notes about this source (optional)"
          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:focus:border-blue-600 focus:ring rounded focus:outline-none focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 dark:bg-gray-700 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out resize-y min-h-[80px] max-h-[400px]"
          maxLength={500} // Limit to 500 characters
        />
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
          {notes.length}/500
        </div>
      </div>

      {/* --- Render Handle Manager Unconditionally --- */}
      <div>
        <HandleManager
          nodeId={nodeId} // Use the nodeId prop
          handles={handles} // Use handles state
          onChange={handleHandlesChange}
          maxHandles={6} // Adjust as needed
          portalRoot={portalRoot}
        />
      </div>
      {/* --- End Handle Manager --- */}
    </div>
  );
}
